import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Form,
  InputNumber,
  Input,
  Button,
  Space,
  Alert,
  Typography,
  Table,
  Tag,
  message,
} from 'antd';
import {
  bulkImportPackages,
  type BulkImportItem,
  type BulkImportResult,
  type BulkImportItemResult,
} from '@/api/content';

const { Title, Paragraph, Text } = Typography;

/**
 * 套系批量导入页 (series-bulk-import capability).
 *
 * 粘贴一个套系 JSON 数组 + 填 app_id → 预览条数 → 提交 → 结果报告.
 * 幂等 (app_id + code): 重跑同一份只 update, 不重复建. 为创想熊量级 (千级套系) 而做.
 */

const SAMPLE_JSON = `[
  {
    "code": "vintage_hk_80s",
    "name": "港风 80s 复古",
    "workflow_code": "WF_HK_VINTAGE",
    "image_prompt": "1980s Hong Kong neon street portrait, cinematic, film grain",
    "price_cents": 990,
    "diamond_cost": 0,
    "status": "active",
    "samples": [{ "url": "https://cdn.example.com/hk1.jpg" }]
  }
]`;

export function ContentBulkImportPage() {
  const [appId, setAppId] = useState<number | null>(null);
  const [raw, setRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);

  const parsed = parseItems(raw);

  const onSubmit = async () => {
    if (!appId) {
      message.error('请填写 app_id');
      return;
    }
    if (!parsed.ok) {
      message.error(`JSON 解析失败: ${parsed.error}`);
      return;
    }
    if (parsed.items.length === 0) {
      message.error('items 为空');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const res = await bulkImportPackages({ app_id: appId, items: parsed.items });
      setResult(res);
      message.success(`导入完成: 新建 ${res.created} / 更新 ${res.updated} / 失败 ${res.failed}`);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '导入失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>
          套系批量导入
        </Title>
        <Link to="/content">← 返回套系列表</Link>
      </Space>
      <Paragraph type="secondary">
        粘贴套系 JSON 数组。幂等键 = (app_id, code)：重跑同一份只更新、不重复建。
        样片仅首次创建时写入；之后用套系详情页的「样片」管理。
        写真/series 流的套系建议带 image_prompt（英文画面 prompt），导入会自动建模板并挂上，
        这样无需答题即可出图。
      </Paragraph>

      <Form layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item label="App ID" required>
          <InputNumber
            min={1}
            value={appId ?? undefined}
            onChange={(v) => setAppId(typeof v === 'number' ? v : null)}
            placeholder="目标 App 的数字 id"
            style={{ width: 240 }}
          />
        </Form.Item>

        <Form.Item
          label="套系 JSON 数组"
          required
          validateStatus={raw && !parsed.ok ? 'error' : undefined}
          help={raw && !parsed.ok ? parsed.error : `已识别 ${parsed.ok ? parsed.items.length : 0} 条`}
        >
          <Input.TextArea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={14}
            placeholder={SAMPLE_JSON}
            style={{ fontFamily: 'monospace', fontSize: 13 }}
          />
        </Form.Item>

        <Space>
          <Button type="primary" loading={submitting} disabled={!parsed.ok || !appId} onClick={onSubmit}>
            导入 {parsed.ok ? `(${parsed.items.length} 条)` : ''}
          </Button>
          <Button onClick={() => setRaw(SAMPLE_JSON)}>填入示例</Button>
        </Space>
      </Form>

      {result && (
        <div style={{ marginTop: 24 }}>
          <Alert
            type={result.failed > 0 ? 'warning' : 'success'}
            showIcon
            message={
              <Text>
                新建 {result.created} · 更新 {result.updated} · 失败 {result.failed}
              </Text>
            }
            style={{ marginBottom: 16 }}
          />
          <Table<BulkImportItemResult>
            rowKey={(r) => r.code}
            size="small"
            pagination={false}
            dataSource={result.outcomes}
            columns={[
              { title: 'code', dataIndex: 'code' },
              {
                title: '结果',
                dataIndex: 'action',
                render: (a: BulkImportItemResult['action']) => (
                  <Tag color={a === 'created' ? 'green' : a === 'updated' ? 'blue' : 'red'}>{a}</Tag>
                ),
              },
              { title: 'package_id', dataIndex: 'package_id' },
              { title: '错误', dataIndex: 'error' },
            ]}
          />
        </div>
      )}
    </Card>
  );
}

type ParseResult =
  | { ok: true; items: BulkImportItem[] }
  | { ok: false; error: string; items: BulkImportItem[] };

function parseItems(raw: string): ParseResult {
  if (!raw.trim()) return { ok: false, error: '请粘贴 JSON', items: [] };
  try {
    const v = JSON.parse(raw);
    if (!Array.isArray(v)) return { ok: false, error: '顶层必须是数组', items: [] };
    return { ok: true, items: v as BulkImportItem[] };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : '解析失败', items: [] };
  }
}
